/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * CanvassReviewView — Stage 5 coordinator inbox for AAR sign-off.
 *
 * Role wall: the backend requires COORDINATOR+ on GET /canvass/aars and
 * PATCH /canvass/aars/{id}/review, so non-coordinators will get a 403
 * banner here via `formatCanvassError`. That's intentional — this view
 * is explicitly the coordinator cockpit and shouldn't silently downgrade.
 *
 * Substates (internal, not a top-level route each):
 *
 *   'list'    — inbox of pending AARs (default) with a "show reviewed"
 *               toggle. Tap a row → 'detail'.
 *   'detail'  — one AAR expanded: auto-summary for its shift + the four
 *               canvasser-written reflection fields, plus a coordinator
 *               notes textarea and an "Approve" button. Approve PATCHes
 *               /canvass/aars/{id}/review and returns to 'list'.
 *
 * We refetch the list whenever we return to 'list' so newly-reviewed
 * AARs drop out of the pending queue without a manual reload.
 *
 * Auto-summary reuse: we piggyback on GET /canvass/shifts/{shift_id}/aar
 * which returns `AARPrefill` (auto_summary + existing AAR row). That's
 * the same endpoint the canvasser's Stage 4 view hits, so the rollup
 * numbers stay consistent between canvasser and coordinator.
 */
import React, { useEffect, useState } from 'react';
import { MoonStar } from 'lucide-react';

import CloseOutDayModal from '@/components/canvass/CloseOutDayModal';

import {
  formatCanvassError,
  getAARPrefill,
  listAARs,
  listFieldDevices,
  reviewAAR,
  listShiftKnocks,
  photoUrl,
} from '@/api/canvass';
import type {
  AARAutoSummary,
  AARPrefill,
  AARRead,
  FieldDeviceBeaconRead,
  FieldDevicesSummary,
  KnockRead,
} from '@/types/canvass';
import { InfoTip } from '@/components/InfoTip';
import {
  OnboardingModal,
  OnboardingHelpButton,
  hasSeenOnboarding,
} from '@/components/OnboardingModal';
import { CoachMarks } from '@/components/onboarding/CoachMarks';
import { CANVASS_REVIEW_TOUR } from '@/components/onboarding/tours';
import { useOnboarding } from '@/hooks/useOnboarding';
import { TableRowSkeleton, Skeleton } from '@/components/Skeleton';

type Phase = 'list' | 'detail';

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function CanvassReviewView({ onBack }: { onBack: () => void }) {
  const [phase, setPhase] = useState<Phase>('list');
  const [selected, setSelected] = useState<AARRead | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forceOnboard, setForceOnboard] = useState(false);
  // "Close out the day" — Easy button #4.
  const [sweepOpen, setSweepOpen] = useState(false);
  const [sweepNotice, setSweepNotice] = useState<string | null>(null);

  // Guided coach-mark tour, sequenced after this view's OnboardingModal —
  // same handoff as CanvassView (explain "what is this", then guide "what do
  // I do"). Completion is stored per-user on the server, so it follows the
  // reviewer across devices. See useOnboarding / tours.ts.
  const { hasSeen, markSeen, seen } = useOnboarding();
  const [tourActive, setTourActive] = useState(false);
  const onboardingDisabled =
    typeof window !== 'undefined' &&
    ((window as any).__PCGOP_DISABLE_ONBOARDING__ === true ||
      (import.meta as any).env?.VITE_DISABLE_ONBOARDING === '1');

  // Modal already dismissed on a prior visit (localStorage) but the tour never
  // done — e.g. the tour shipped later. First-visit instead hands off from the
  // modal's onClose below, so the two never overlap.
  useEffect(() => {
    if (onboardingDisabled) return;
    if (hasSeenOnboarding('canvassreviewview') && !hasSeen(CANVASS_REVIEW_TOUR.id)) {
      setTourActive(true);
    }
    // `seenKey` in the deps so this re-evaluates once /auth/me resolves (or on
    // an account switch) rather than reading a stale/empty seen list on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen.join('|')]);

  function toDetail(a: AARRead) {
    setError(null);
    setSelected(a);
    setPhase('detail');
  }

  function toList() {
    setError(null);
    setSelected(null);
    setPhase('list');
  }

  return (
    <div className="space-y-4">
      <OnboardingModal
        viewKey="canvassreviewview"
        title="Canvass AAR Inbox"
        intro="Coordinator-only review queue for after-action reports. Each row is an AAR a canvasser filed at the end of their shift; you'll see their reflection notes plus an auto-summary of what they actually did at the doors. Approve to clear it from the queue."
        termIds={['aar', 'canvass_shift', 'briefing', 'outcome_categories', 'field_devices']}
        workflows={[
          'Glance at the Field Devices strip — amber queued counts mean knocks are stuck offline; stale (red) devices mean a phone may have died.',
          'Skim the auto-summary for outcome counts before reading the canvasser notes.',
          'Add coordinator notes when you spot a pattern worth reinforcing.',
          'Approve to remove from the queue — approved AARs feed the program retro.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => {
          const wasForced = forceOnboard;
          setForceOnboard(false);
          // First-visit dismissal (NOT a Help-button reopen) hands off to the
          // guided tour. A forced reopen just closes.
          if (!wasForced && !onboardingDisabled && !hasSeen(CANVASS_REVIEW_TOUR.id)) {
            setTourActive(true);
          }
        }}
      />

      {tourActive && phase === 'list' && (
        <CoachMarks
          tour={CANVASS_REVIEW_TOUR}
          onFinish={() => {
            setTourActive(false);
            void markSeen(CANVASS_REVIEW_TOUR.id);
          }}
          // Anchors never mounted — hide without marking seen so it re-offers.
          onAbort={() => setTourActive(false)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-fg-muted uppercase tracking-wide">
            Stage 5 · coordinator review
          </div>
          <h1
            className="text-3xl font-bold text-fg-primary mt-1 flex items-center gap-2"
            data-tour="review-inbox"
          >
            Canvass AAR Inbox <InfoTip termId="aar" />
          </h1>
        </div>
        <div className="flex gap-2 items-center" data-tour="onboarding-help">
          {/* "Close out the day" (Easy button #4). It lives here because this
              is the coordinator cockpit and the gate matches: both this view
              and POST /canvass/shifts/close-stale are COORDINATOR+. Stale
              IN_PROGRESS shifts are exactly what makes this inbox lie. */}
          <button
            type="button"
            onClick={() => setSweepOpen(true)}
            className="inline-flex shrink-0 items-center gap-2 rounded-md border border-border-subtle px-3 py-2 text-sm font-semibold text-fg-primary hover:bg-bg-base"
          >
            <MoonStar size={16} />
            Close out the day
          </button>
          <OnboardingHelpButton onClick={() => setForceOnboard(true)} />
        </div>
      </div>

      <div data-tour="review-field-devices">
        <FieldDevicesWidget />
      </div>

      {sweepOpen && (
        <CloseOutDayModal
          onClose={() => setSweepOpen(false)}
          onSwept={(closed) => {
            setSweepOpen(false);
            setSweepNotice(
              closed === 0
                ? 'Nothing needed closing.'
                : `Closed ${closed} forgotten shift${closed === 1 ? '' : 's'}.`,
            );
          }}
        />
      )}

      {sweepNotice && (
        <div className="rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-fg-secondary">
          {sweepNotice}
        </div>
      )}

      {error && <ErrorBanner message={error} onClear={() => setError(null)} />}

      {phase === 'list' && (
        <InboxList onOpen={toDetail} onError={setError} />
      )}
      {phase === 'detail' && selected && (
        <AARDetail
          aar={selected}
          onApproved={() => {
            toList();
          }}
          onError={setError}
          onBack={toList}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field devices widget — coordinator queue observability
// ---------------------------------------------------------------------------

function FieldDevicesWidget() {
  const [summary, setSummary] = useState<FieldDevicesSummary | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);

  async function refresh() {
    try {
      const data = await listFieldDevices(5);
      setSummary(data);
      setLoadError(false);
      const now = new Date();
      setLastRefresh(now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch {
      // Coordinator may not have the role, or backend is unreachable.
      // Silently hide the widget rather than blocking the AAR inbox.
      setLoadError(true);
    }
  }

  useEffect(() => {
    refresh();
    // Poll every 30 s so the coordinator sees near-real-time updates.
    const iv = setInterval(refresh, 30_000);
    return () => clearInterval(iv);
  }, []);

  // Don't render anything if we got a 403 or network error — the widget
  // is additive, not critical.
  if (loadError || !summary) return null;

  // Don't render if there are zero beacons (nobody in the field yet).
  if (summary.devices.length === 0) return null;

  function ago(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m ago`;
  }

  return (
    <div className="bg-bg-raised rounded-lg border border-border-base p-3">
      <div className="w-full flex items-center justify-between">
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="flex-1 flex items-center justify-between text-left gap-3 min-h-11 sm:min-h-0 py-2 sm:py-0"
        >
          <div className="flex items-center gap-3">
            <span className="text-xs text-fg-muted uppercase tracking-wide font-semibold">
              Field Devices
            </span>
            {lastRefresh && (
              <span className="text-[10px] text-fg-muted font-mono">
                {lastRefresh}
              </span>
            )}
          </div>
          {collapsed && (
            <span className="text-xs font-mono text-fg-secondary">
              {summary.devices.length} active · {summary.total_queued} queued · {summary.stale_count} stale
            </span>
          )}
        </button>
        <div className="flex items-center gap-3 ml-3">
          <button
            onClick={() => {
              refresh();
            }}
            className="text-fg-secondary hover:text-fg-primary text-[10px] inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 px-2 sm:px-0"
          >
            Refresh now
          </button>
          <button
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand field devices' : 'Collapse field devices'}
            className="text-fg-muted text-xs inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
          >
            {collapsed ? '▸' : '▾'}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="mt-3 flex gap-2 flex-wrap text-xs font-mono text-fg-secondary">
            <span className="bg-bg-base border border-border-base rounded px-2 py-1">
              {summary.devices.length} active
            </span>
            {summary.total_queued > 0 && (
              <span className="bg-status-warning-bg/30 border border-status-warning rounded px-2 py-1 text-status-warning">
                {summary.total_queued} queued
              </span>
            )}
            {summary.stale_count > 0 && (
              <span className="bg-status-danger-bg/30 border border-status-danger rounded px-2 py-1 text-status-danger">
                {summary.stale_count} stale
              </span>
            )}
          </div>
          <div className="mt-2 space-y-1">
            {summary.devices.map((d) => {
              const staleMs = Date.now() - new Date(d.last_seen_at).getTime();
              const isStale = staleMs > 5 * 60_000;

              return (
                <div
                  key={d.id}
                  className={`flex items-center justify-between px-2 py-1.5 rounded text-sm ${
                    isStale
                      ? 'bg-status-danger-bg/20 border border-status-danger'
                      : 'bg-bg-base border border-border-base'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${
                        isStale ? 'bg-status-danger-bg' : 'bg-status-success-bg'
                      }`}
                    />
                    <span className="text-fg-primary truncate">
                      {d.user_name || `User #${d.user_id}`}
                    </span>
                    {d.shift_id && (
                      <span className="text-[10px] text-fg-muted font-mono">
                        shift #{d.shift_id}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {d.queued_count > 0 ? (
                      <span className="text-xs font-bold text-status-warning">
                        {d.queued_count} pending
                      </span>
                    ) : (
                      <span className="text-xs text-fg-muted">
                        0 pending
                      </span>
                    )}
                    <span
                      className={`text-[11px] font-mono ${
                        isStale ? 'text-status-danger' : 'text-fg-muted'
                      }`}
                    >
                      {ago(d.last_seen_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inbox list
// ---------------------------------------------------------------------------

function InboxList({
  onOpen,
  onError,
}: {
  onOpen: (a: AARRead) => void;
  onError: (m: string) => void;
}) {
  const [pendingOnly, setPendingOnly] = useState(true);
  const [aars, setAars] = useState<AARRead[] | null>(null);

  async function refresh() {
    setAars(null);
    try {
      const rows = await listAARs({ pending_only: pendingOnly, limit: 100 });
      setAars(rows);
    } catch (e) {
      onError(formatCanvassError(e));
      setAars([]);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOnly]);

  return (
    <div className="bg-bg-raised rounded-lg border border-border-base p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-fg-muted uppercase tracking-wide">
          {pendingOnly ? 'Pending review' : 'All submitted AARs'}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPendingOnly(true)}
            className={`px-3 py-1 rounded text-xs font-semibold inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 ${
              pendingOnly
                ? 'bg-accent text-white'
                : 'bg-bg-elevated text-fg-secondary hover:bg-border-subtle'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setPendingOnly(false)}
            className={`px-3 py-1 rounded text-xs font-semibold inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 ${
              !pendingOnly
                ? 'bg-accent text-white'
                : 'bg-bg-elevated text-fg-secondary hover:bg-border-subtle'
            }`}
          >
            All
          </button>
          <button
            onClick={refresh}
            className="px-3 py-1 rounded text-xs font-semibold bg-bg-elevated hover:bg-border-subtle text-fg-secondary inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {aars === null && (
        <TableRowSkeleton rows={5} cols={5} />
      )}
      {aars !== null && aars.length === 0 && (
        <div className="text-fg-muted text-sm py-6 text-center">
          {pendingOnly
            ? 'Inbox zero. No AARs awaiting review.'
            : 'No AARs have been submitted yet.'}
        </div>
      )}
      {aars !== null && aars.length > 0 && (
        <div className="space-y-2">
          {aars.map((a) => (
            <button
              key={a.id}
              onClick={() => onOpen(a)}
              className="block w-full text-left bg-bg-base hover:bg-bg-elevated/60 rounded border border-border-base p-3"
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-fg-primary">
                  Shift #{a.canvass_shift_id}
                </div>
                <ReviewBadge aar={a} />
              </div>
              <div className="text-xs text-fg-muted mt-1">
                Submitted {formatTime(a.submitted_at)}
              </div>
              {a.safety_issues && (
                <div className="text-xs text-status-warning mt-1">
                  ⚠️ Safety notes flagged
                </div>
              )}
              {a.went_well && (
                <div className="text-xs text-fg-secondary mt-1 line-clamp-2">
                  {a.went_well.slice(0, 160)}
                  {a.went_well.length > 160 ? '…' : ''}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewBadge({ aar }: { aar: AARRead }) {
  if (aar.coordinator_reviewed_at) {
    return (
      <span className="text-xs text-status-success bg-status-success-bg/30 border border-status-success rounded px-2 py-0.5">
        ✓ Reviewed
      </span>
    );
  }
  return (
    <span className="text-xs text-status-warning bg-status-warning-bg/30 border border-status-warning rounded px-2 py-0.5">
      ● Pending
    </span>
  );
}

// ---------------------------------------------------------------------------
// Detail / approval screen
// ---------------------------------------------------------------------------

function AARDetail({
  aar,
  onApproved,
  onError,
  onBack,
}: {
  aar: AARRead;
  onApproved: (updated: AARRead) => void;
  onError: (m: string) => void;
  onBack: () => void;
}) {
  const [prefill, setPrefill] = useState<AARPrefill | null>(null);
  const [notes, setNotes] = useState(aar.coordinator_notes || '');
  const [saving, setSaving] = useState(false);
  const [knocks, setKnocks] = useState<KnockRead[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getAARPrefill(aar.canvass_shift_id);
        if (!cancelled) setPrefill(p);
      } catch (e) {
        if (!cancelled) {
          onError(formatCanvassError(e));
          setPrefill(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aar.canvass_shift_id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const k = await listShiftKnocks(aar.canvass_shift_id);
        if (!cancelled) setKnocks(k);
      } catch (e) {
        if (!cancelled) {
          // Silently fail for knocks — photos are optional
          setKnocks([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aar.canvass_shift_id]);

  async function approve() {
    setSaving(true);
    try {
      const updated = await reviewAAR(aar.id, {
        coordinator_notes: notes || undefined,
      });
      onApproved(updated);
    } catch (e) {
      onError(formatCanvassError(e));
    } finally {
      setSaving(false);
    }
  }

  const alreadyReviewed = !!aar.coordinator_reviewed_at;

  return (
    <div className="space-y-4">
      <div className="bg-bg-raised rounded-lg border border-border-base p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs text-fg-muted uppercase tracking-wide">
              AAR #{aar.id} · Shift #{aar.canvass_shift_id}
            </div>
            <h2 className="text-2xl font-bold text-fg-primary mt-1">
              {alreadyReviewed ? 'Previously reviewed' : 'Pending sign-off'}
            </h2>
            <div className="text-xs text-fg-muted mt-1">
              Submitted {formatTime(aar.submitted_at)}
              {alreadyReviewed && (
                <> · Reviewed {formatTime(aar.coordinator_reviewed_at)}</>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Auto summary */}
      <div className="bg-bg-raised rounded-lg border border-border-base p-4">
        <div className="text-xs text-fg-muted uppercase tracking-wide mb-2">
          Auto-summary (server-computed)
        </div>
        {prefill === null ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : (
          <AutoSummaryBlock summary={prefill.auto_summary} />
        )}
      </div>

      {/* Door photos */}
      {knocks && knocks.length > 0 && (
        (() => {
          const knocksWithPhotos = knocks.filter(
            (k) => k.response?.photo_path
          );
          if (knocksWithPhotos.length === 0) return null;
          return (
            <div className="bg-bg-raised rounded-lg border border-border-base p-4">
              <div className="text-xs text-fg-muted uppercase tracking-wide mb-3">
                Door photos
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {knocksWithPhotos.map((k) => (
                  <div
                    key={k.id}
                    className="flex flex-col items-center shrink-0"
                  >
                    <img
                      src={photoUrl(k.response!.photo_path!)}
                      alt={`Door #${k.route_stop_id}`}
                      className="rounded border border-border-base h-24 w-auto object-contain cursor-pointer"
                    />
                    <div className="text-[10px] text-fg-muted mt-1">
                      Stop #{k.route_stop_id}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()
      )}

      {/* Canvasser reflections */}
      <div className="bg-bg-raised rounded-lg border border-border-base p-4 space-y-3">
        <div className="text-xs text-fg-muted uppercase tracking-wide">
          Canvasser reflection
        </div>
        <ReadOnlyField label="What went well" value={aar.went_well} />
        <ReadOnlyField label="Anything unexpected" value={aar.unexpected} />
        <ReadOnlyField
          label="Safety issues"
          value={aar.safety_issues}
          tone={aar.safety_issues ? 'warn' : 'normal'}
        />
        <ReadOnlyField label="Turf notes" value={aar.turf_notes} />
      </div>

      {/* Coordinator notes */}
      <div className="bg-bg-raised rounded-lg border border-border-base p-4 space-y-2">
        <div className="text-xs text-fg-muted uppercase tracking-wide">
          Coordinator notes (optional)
        </div>
        <textarea
          className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Coaching, follow-ups, escalations — the canvasser will see this."
          disabled={saving}
        />
        <button
          disabled={saving}
          onClick={approve}
          className="w-full bg-accent hover:bg-accent-hover disabled:opacity-40 text-white font-bold py-3 rounded-lg"
        >
          {saving
            ? 'Saving…'
            : alreadyReviewed
            ? 'Re-stamp review'
            : '✓ Approve & sign off'}
        </button>
        {alreadyReviewed && (
          <div className="text-xs text-fg-muted">
            Re-stamping updates the review timestamp and coordinator. The
            original submission is not modified.
          </div>
        )}
      </div>
    </div>
  );
}

function ReadOnlyField({
  label,
  value,
  tone = 'normal',
}: {
  label: string;
  value: string | null | undefined;
  tone?: 'normal' | 'warn';
}) {
  return (
    <div>
      <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">
        {label}
      </div>
      <div
        className={`text-sm whitespace-pre-wrap rounded border px-3 py-2 ${
          tone === 'warn'
            ? 'text-status-warning bg-status-warning-bg/20 border-status-warning'
            : 'text-fg-secondary bg-bg-base border-border-base'
        }`}
      >
        {value && value.trim() ? value : <em className="text-fg-muted">—</em>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auto-summary block (mirrors the canvasser view — same shape, same math)
// ---------------------------------------------------------------------------

function AutoSummaryBlock({ summary }: { summary: AARAutoSummary }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Doors attempted" value={summary.total_doors_attempted} />
        <Stat
          label="Avg min / door"
          value={
            summary.avg_minutes_per_door !== null
              ? summary.avg_minutes_per_door.toFixed(1)
              : '—'
          }
        />
        <Stat label="Escalations" value={summary.escalation_count} />
        <Stat
          label="Outcomes"
          value={summary.outcomes.reduce((n, o) => n + o.count, 0)}
        />
      </div>

      {summary.outcomes.length > 0 && (
        <div>
          <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">
            Outcome mix
          </div>
          <div className="flex flex-wrap gap-2">
            {summary.outcomes.map((o) => (
              <span
                key={o.outcome}
                className="text-xs bg-bg-base border border-border-base rounded px-2 py-1 text-fg-secondary"
              >
                {o.outcome}: <strong>{o.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {summary.lean_distribution.length > 0 && (
        <div>
          <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">
            Candidate lean
          </div>
          <div className="flex flex-wrap gap-2">
            {summary.lean_distribution.map((l) => (
              <span
                key={l.lean}
                className="text-xs bg-bg-base border border-border-base rounded px-2 py-1 text-fg-secondary"
              >
                {l.lean}: <strong>{l.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {summary.issue_frequency.length > 0 && (
        <div>
          <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">
            Top issues mentioned
          </div>
          <div className="flex flex-wrap gap-2">
            {summary.issue_frequency.map((i) => (
              <span
                key={i.slug}
                className="text-xs bg-bg-base border border-border-base rounded px-2 py-1 text-fg-secondary"
              >
                {i.slug}: <strong>{i.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-bg-base rounded border border-border-base px-3 py-2">
      <div className="text-xs text-fg-muted uppercase tracking-wide">
        {label}
      </div>
      <div className="text-lg font-bold text-fg-primary">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error banner
// ---------------------------------------------------------------------------

function ErrorBanner({
  message,
  onClear,
}: {
  message: string;
  onClear: () => void;
}) {
  return (
    <div className="bg-status-danger-bg/40 border border-status-danger rounded p-3 flex items-start justify-between gap-3">
      <div className="text-sm text-status-danger">{message}</div>
      <button
        onClick={onClear}
        className="text-status-danger hover:text-fg-primary text-xs inline-flex items-center justify-center min-h-11 sm:min-h-0 px-2 sm:px-0"
      >
        dismiss
      </button>
    </div>
  );
}

export default CanvassReviewView;
